import axios, { type AxiosRequestConfig } from 'axios';
import type { TenantMetadata } from '../types';
import { assertTenantMetadata } from '../validators/assertTenantMetadata';

type TenantCmsApiClient = {
	get<TData>(
		url: string,
		config?: AxiosRequestConfig
	): Promise<{ data: TData }>;
};

type TenantApiClientContract = {
	getTenantMetadata(
		partnerIdn: string,
		cmsApiUrl?: string
	): Promise<TenantMetadata>;
};

const resolveBaseUrl = (cmsApiUrl?: string): string | undefined => {
	if (cmsApiUrl) {
		return cmsApiUrl;
	}

	if (typeof process !== 'undefined' && process.env) {
		return process.env.VITE_CMS_API_URL;
	}

	return undefined;
};

// baseURL is resolved per request (not frozen at module load) so the value can
// come from runtime config (appConfig -> cmsApiUrl) instead of build-time env.
const cmsApi = axios.create({
	timeout: 5000
});

export const createTenantApiClient = (
	cmsApiClient: TenantCmsApiClient
): TenantApiClientContract => ({
	async getTenantMetadata(
		partnerIdn: string,
		cmsApiUrl?: string
	): Promise<TenantMetadata> {
		const response = await cmsApiClient.get<unknown>('/api/public/partners', {
			baseURL: resolveBaseUrl(cmsApiUrl),
			params: {
				id: partnerIdn
			}
		});

		assertTenantMetadata(response.data, partnerIdn, 'CMS response');

		return response.data;
	}
});

export const TenantApiClient = createTenantApiClient(cmsApi);
